import { DatePipe } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild
} from '@angular/core';

import { ExportToCSV } from '@molteni/export-csv';
import { TranslateService } from '@ngx-translate/core';
import { DatatableComponent } from '@swimlane/ngx-datatable';
import { TableColumn } from '@swimlane/ngx-datatable/src/types';

import { Column, DatatableButton } from 'app/models';

interface DatatableSort {
  dir: 'asc' | 'desc';
  prop: string;
}

interface DatatablePageEvent {
  count: number;
  pageSize: number;
  limit: number;
  offset: number;
}

interface DatatableSortEvent {
  sorts: DatatableSort[];
  column: TableColumn;
  prevValue: 'asc' | 'desc' | undefined;
  newValue: 'asc' | 'desc';
}

let cbmTableIdCounter = 0;

@Component({
  selector: 'cbm-table',
  templateUrl: './cbm-table.component.html',
  styleUrls: ['./cbm-table.component.scss']
})
export class CbmTableComponent<T> implements OnChanges, OnInit {
  static exporter = new ExportToCSV();

  @ViewChild(DatatableComponent) table: DatatableComponent;
  @ViewChild('filterHeaderTmpl') filterHeaderTmpl: TemplateRef<any>;
  @ViewChild('labelHeaderTmpl') labelHeaderTmpl: TemplateRef<any>;
  @ViewChild('cellDateTmpl') cellDateTmpl: TemplateRef<any>;
  @ViewChild('buttonTmpl') buttonTmpl: TemplateRef<any>;

  @Input() private data: T[];
  @Input() private columnDefs: Column[];

  @Input() public id: string;
  @Input() public buttons: DatatableButton[] = [];
  @Input() public downloadable = false;

  @Output() onRowClick = new EventEmitter<T>();
  private readonly STRING_LENGTH_LIMIT = 250;
  public cbmTableId = `cbm-table-${cbmTableIdCounter++}`;

  // public copy of 'data', to show processed results (for filters, etc)
  public rows: any[] = [];
  public columns: TableColumn[];
  public pageSize = 30;
  public messages: any = {};

  /*
    Filters per column, updated as you type, for example
    {
        age: 5,
        name: 'John'
    }
    */
  public generalFilter: string;
  public filters: any = {};
  public regexFilter: any = {};

  private filterTimeout: number;

  constructor(private readonly translateService: TranslateService, private readonly datePipe: DatePipe) {}

  ngOnInit() {
    this.applyStoredTableState();
    this.initLanguage();

    this.columns = this.initColumns();
  }

  ngOnChanges(changes: SimpleChanges) {
    let shouldFilter = false;
    let shouldInitColumns = false;

    if (changes.buttons) {
      if (!changes.buttons.isFirstChange() || changes.buttons.currentValue.length > 0) {
        shouldInitColumns = true;
      }
    }

    if (changes.data) {
      if (!changes.data.isFirstChange() || changes.data.currentValue.length > 0) {
        shouldInitColumns = true;
        shouldFilter = true;
      }
    }

    if (changes.columns) {
      if (!changes.columns.isFirstChange() || changes.columns.currentValue.length > 0) {
        shouldInitColumns = true;
        shouldFilter = true;
      }
    }

    if (shouldFilter) {
      this.filterTable(true);
    }
    if (shouldInitColumns) {
      this.columns = this.initColumns();
    }
  }

  public onActivate(event: any) {
    // activates for multiple events, hover, click, etc

    const eventType = event.type;
    const col = event.column;
    const row = event.row;

    if (eventType === 'click') {
      if (typeof col.button !== 'undefined') {
        this.activateButtonCallback(col.prop, row._originalData);
      } else {
        this.onRowClick.emit(row._originalData);
      }
    }
  }

  public getListOfPropertiesOfDisplayedData(property: string[]): any[] {
    return this.table._rows.map(r => {
      const v = {};
      for (const prop of property) {
        v[prop] = r[prop];
      }
      return v;
    });
  }

  public onPageSizeChange(): void {
    this.storeCurrentTableState();
  }

  public onPage(event: DatatablePageEvent): void {
    this.storeCurrentTableState();
  }

  public onSort(event: DatatableSortEvent): void {
    this.storeCurrentTableState();
  }

  private isFilterable(cd: Column): boolean {
    return cd.filterable && cd.type !== 'date';
  }

  private getCellTemplateForColumn(cd: Column): TemplateRef<any> {
    if (cd.type === 'date') {
      return this.cellDateTmpl;
    }
    return null;
  }

  private initColumns(): TableColumn[] {
    const columns: TableColumn[] = this.columnDefs.map(cd => {
      return {
        name: cd.property,
        label: cd.label,
        type: cd.type,
        headerTemplate: this.isFilterable(cd) ? this.filterHeaderTmpl : this.labelHeaderTmpl,
        cellTemplate: this.getCellTemplateForColumn(cd),
        flexGrow: typeof cd.flexGrow !== 'undefined' ? cd.flexGrow : 2,
        callback: cd.valueCallback
      };
    });

    for (const db of this.buttons) {
      if (this.data.some(d => db.showIf(d))) {
        const buttonColumn = {
          name: '',
          label: db.label,
          prop: db.id,
          button: db,
          headerTemplate: this.labelHeaderTmpl,
          cellTemplate: this.buttonTmpl,
          flexGrow: 1,
          showIf: db.showIf
        };
        columns.push(buttonColumn);
      }
    }

    return columns;
  }

  private initLanguage(): any {
    this.translateService.get('DATATABLE.NO_DATA').subscribe(translation => (this.messages.emptyMessage = translation));

    this.translateService.get('DATATABLE.TOTAL').subscribe(translation => (this.messages.totalMessage = translation));
  }

  private activateButtonCallback(id: string, row: T): void {
    for (const db of this.buttons) {
      if (db.id === id) {
        db.action(row);
        return;
      }
    }
  }

  public setRegexFilter(property: string, values: any) {
    this.regexFilter = {};
    this.regexFilter[property] = values;
    this.filterTable();
  }

  public resetRegexFilter(): void {
    this.regexFilter = {};
    this.filterTable();
  }

  public filterTableAsync(): void {
    if (typeof this.filterTimeout === 'number') {
      clearTimeout(this.filterTimeout);
      this.filterTimeout = undefined;
    }

    // debounce the filter (don't filter on every character that's typed, but wait until the user finished typing)
    this.filterTimeout = window.setTimeout(() => this.filterTable(), 333);
  }

  /**
   * filterTable assumes the data is already transformed with `transformData`
   */
  public filterTable(preventPageReset?: boolean): void {
    if (typeof this.filterTimeout === 'number') {
      clearTimeout(this.filterTimeout);
      this.filterTimeout = undefined;
    }

    // filter data for each type of filter
    this.rows = this.initRows(this.data).filter(r => {
      // filter per field
      // note, all column filters must pass. So don't return true early
      for (const prop in this.filters) {
        if (this.filters.hasOwnProperty(prop)) {
          const filter = this.filters[prop];
          if (typeof filter !== 'undefined' && filter !== '' && typeof r[prop] !== 'undefined') {
            if (r[prop] === null) {
              return false;
            } else if (r[prop].toString().toLowerCase().indexOf(filter.toLowerCase()) === -1) {
              return false;
            }
          }
        }
      }

      // Filter button values
      for (const prop in this.regexFilter) {
        if (this.regexFilter.hasOwnProperty(prop)) {
          const regexFilter = this.regexFilter[prop];
          if (typeof r[prop] === 'undefined' || r[prop] === null || !r[prop].toLowerCase().match(regexFilter)) {
            return false;
          }
        }
      }

      // general filter over all fields
      if (this.generalFilter) {
        for (const prop in r) {
          if (r.hasOwnProperty(prop) && prop !== '_originalData') {
            if (
              typeof r[prop] !== 'undefined' &&
              r[prop] !== null &&
              r[prop].toString().toLowerCase().indexOf(this.generalFilter.toLowerCase()) !== -1
            ) {
              return true;
            }
          }
        }
        return false;
      }

      // include in results if all filters match
      return true;
    });

    if (!preventPageReset) {
      // Whenever the filter changes, go back to the first page
      if (this.table) {
        this.table.offset = 0;
      }

      this.storeCurrentTableState();
    }
  }

  private initRows(data: T[]) {
    if (data && Array.isArray(data) && data.length > 0) {
      return data.map(d => this.mapRowData(d, this.columnDefs, this.translateService));
    } else {
      return [];
    }
  }

  private mapRowData(d: T, props: Column[], translateService: TranslateService) {
    const row = {};
    row['_originalData'] = d;

    for (const prop of props) {
      if (prop.type === 'string' && d[prop.property] != null) {
        row[prop.property] = this.handleStringType(d, prop);
      } else if (prop.type === 'translatable') {
        row[prop.property] = this.handleTranslatableType(d, prop, translateService);
      } else {
        row[prop.property] = d[prop.property];
      }
    }
    return row;
  }

  private handleStringType(d: T, prop: Column): string {
    const value = d[prop.property];
    if (value.length > this.STRING_LENGTH_LIMIT) {
      return `${value.substring(0, this.STRING_LENGTH_LIMIT)}...`;
    } else if (prop.valueCallback) {
      return prop.valueCallback(d);
    }
    return value;
  }

  private handleTranslatableType(d: T, prop: Column, translateService: TranslateService): string | null {
    if (prop.valueCallback) {
      const translatableValue = prop.valueCallback(d);
      return translatableValue ? translateService.instant(translatableValue) : null;
    }
    return d[prop.property] != null ? translateService.instant(d[prop.property]) : null;
  }

  /**
   * Stores the current table state (limit, offset, sorts, filters, generalFilter, buttonFilter) in localStorage.
   * This is triggered on the following events: page, sort, filter
   */
  private storeCurrentTableState(): void {
    const state = {
      limit: this.pageSize,
      offset: this.table.offset,
      sorts: this.table.sorts,
      filters: this.filters,
      generalFilter: this.generalFilter,
      buttonFilter: this.regexFilter
    };

    localStorage.setItem(this.id, JSON.stringify(state));
  }

  /**
   * Applies the stored table state (in localStorage) to the current table
   */
  private applyStoredTableState(): void {
    const state = JSON.parse(localStorage.getItem(this.id));

    if (state) {
      this.pageSize = state.limit;
      this.table.offset = state.offset;
      this.table.sorts = state.sorts;
      this.filters = state.filters;
      this.generalFilter = state.generalFilter;

      this.filterTable(true);
    }
  }

  public exportCsv(): void {
    const dateTypeColumns = this.columnDefs.filter(cd => cd.type === 'date');

    const exportData = this.rows.map(r => {
      const transformed: any = {};

      // copy all fields
      for (const property in r) {
        if (r.hasOwnProperty(property)) {
          transformed[property] = r[property];
        }
      }

      // transform the date fields
      for (const { property } of dateTypeColumns) {
        transformed[property] = this.transformDate(r[property]);
      }

      delete transformed['_originalData'];

      return transformed;
    });

    CbmTableComponent.exporter.exportAllToCSV(exportData, `${this.id}.csv`);
  }

  private transformDate(date: any): string {
    return this.datePipe.transform(date, 'dd/MM/yyyy HH:mm');
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/datatable/cbm-table.component.ts